import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Clip create / edit modal.
 *
 * The modal hosts both the metadata form (title, manual Bunny video ID
 * paste-in) and the Bunny Stream video upload (TUS resumable).
 *
 * CREATE-FROM-UPLOAD: in create mode (no `initial`), picking/dropping a
 * video file auto-creates the clip FIRST (same `createClip` call the
 * Save button uses, validated on the `title` field), then immediately
 * continues into the TUS upload flow IN THE SAME OPEN MODAL. The modal
 * silently switches to edit mode by promoting the newly-created clip
 * to `effectiveClip` state. The parent's list refreshes on close via
 * `onDone` whenever any mutation occurred (auto-create, explicit save,
 * or a successful upload).
 */
import React, { useRef, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Progress, Space, Typography, Upload } from 'antd';
import { InboxOutlined, VideoCameraOutlined } from '@ant-design/icons';
import * as tus from 'tus-js-client';
import { createClip, getClipUploadToken, updateClip } from '../../api';
import { notifyError, notifySuccess } from '../errors';
const { Text } = Typography;
export function ClipModal({ open, onClose, onDone, lessonId, initial }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const initialValues = initial ? { title: initial.title, bunny_video_id: initial.bunny_video_id } : undefined;
    const formKey = initial ? `edit-${initial.id}` : 'create';
    // `effectiveClip` is the clip the modal is currently editing. It starts
    // from the `initial` prop; the create-from-upload flow sets it after a
    // successful auto-create so the rest of the modal (submit path, upload
    // flow, close behavior) behaves as if we were in edit mode all along.
    const [effectiveClip, setEffectiveClip] = useState(initial ?? null);
    // Any mutation (auto-create, explicit save, successful upload) flips
    // this to `true`. On close via Cancel/X/backdrop, we route through
    // `onDone` (which triggers the parent reload) instead of `onClose`,
    // so the parent list picks up the new clip even if the operator
    // never clicks "שמור".
    const [wasMutated, setWasMutated] = useState(false);
    // Upload state.
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [uploadedGuid, setUploadedGuid] = useState(null);
    const uploadRef = useRef(null);
    const submit = async () => {
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return;
        }
        setSubmitting(true);
        try {
            const payload = { title: v.title.trim(), bunny_video_id: v.bunny_video_id?.trim() || null };
            if (effectiveClip) {
                await updateClip(effectiveClip.id, payload);
                notifySuccess('קליפ עודכן');
            }
            else {
                await createClip({ ...payload, lesson_id: lessonId });
                notifySuccess('קליפ נוצר');
            }
            setWasMutated(true);
            onDone();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setSubmitting(false);
        }
    };
    const abortUpload = () => {
        if (uploadRef.current) {
            uploadRef.current.abort(true).catch(() => { });
            uploadRef.current = null;
        }
        setUploading(false);
        setUploadPct(0);
    };
    const closeModal = () => {
        abortUpload();
        // If anything was persisted during this modal session (auto-create,
        // explicit save, or a successful upload), the parent needs to
        // reload. Routing through `onDone` triggers the parent's reload
        // callback; otherwise this is a cosmetic close.
        if (wasMutated)
            onDone();
        else
            onClose();
    };
    /**
     * If the modal is already editing a persisted clip, return it. Otherwise
     * (create mode) validate the title field and auto-create the clip via
     * the same `createClip` call Save uses, then promote it to
     * `effectiveClip` so the rest of the modal is in edit mode. Returns
     * null on validation failure or server error (the api client toasts
     * the error for us).
     */
    async function ensureClipCreated() {
        if (effectiveClip)
            return effectiveClip;
        let v;
        try {
            v = await form.validateFields(['title']);
        }
        catch {
            return null;
        }
        const title = v.title.trim();
        try {
            const res = await createClip({ lesson_id: lessonId, title });
            notifySuccess('קליפ נוצר');
            // Synthesize a minimal AdminClip — the only fields used downstream
            // from here are `id` and `title`. `order_index` is server-assigned;
            // the modal doesn't need it. The parent will reload on close and
            // see the authoritative row.
            const created = {
                id: res.id,
                lesson_id: lessonId,
                title,
                bunny_video_id: null,
                order_index: 0,
            };
            setEffectiveClip(created);
            setWasMutated(true);
            return created;
        }
        catch {
            return null; // client auto-surfaces
        }
    }
    const handleFileChosen = async (file, clip) => {
        setUploadError(null);
        setUploadPct(0);
        setUploading(true);
        // 1. Server mints the TUS signature + (if needed) creates the Bunny
        //    video record. Signature doesn't carry the Stream API key.
        let token;
        try {
            token = await getClipUploadToken(clip.id);
        }
        catch (err) {
            setUploading(false);
            notifyError(err, 'שגיאת קבלת מפתח העלאה');
            return;
        }
        // 2. Direct browser → Bunny TUS. tus-js-client handles chunking +
        //    retry delays; we just forward the signature headers Bunny
        //    expects per https://docs.bunny.net/reference/tus-resumable-uploads.
        const upload = new tus.Upload(file, {
            endpoint: token.upload_endpoint,
            retryDelays: [0, 1000, 3000, 5000, 10000],
            // `metadata` is Base64-encoded by tus-js-client and sent as the
            // standard `Upload-Metadata` header. Bunny reads `filetype` for
            // the MIME + `title` for the dashboard display name.
            metadata: {
                filetype: file.type || 'video/mp4',
                title: clip.title,
            },
            headers: {
                AuthorizationSignature: token.authorization_signature,
                AuthorizationExpire: String(token.authorization_expire),
                VideoId: token.video_id,
                LibraryId: token.library_id,
            },
            onProgress(bytesUploaded, bytesTotal) {
                // Progress bar reflects uploaded/total of the user-selected file.
                // tus-js-client fires this once per chunk ACK.
                setUploadPct(bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0);
            },
            onSuccess() {
                uploadRef.current = null;
                setUploading(false);
                setUploadPct(100);
                setUploadedGuid(token.video_id);
                setWasMutated(true);
                // Sync the manual-paste field so a subsequent "שמור" PATCH
                // doesn't overwrite the GUID the server just persisted.
                form.setFieldValue('bunny_video_id', token.video_id);
                notifySuccess('הסרטון הועלה בהצלחה');
            },
            onError(err) {
                uploadRef.current = null;
                setUploading(false);
                // Keep the % stuck at its last value so the operator can see
                // where it died. notifyError toasts; Alert below shows inline.
                setUploadError(err.message || 'שגיאה לא ידועה');
                notifyError(err, 'שגיאה בהעלאת הסרטון');
            },
        });
        uploadRef.current = upload;
        upload.start();
    };
    // Dragger adapter: validate type, auto-create the clip in create mode
    // (title validated first — invalid title => no create, no upload),
    // then hand the File to the existing `handleFileChosen` TUS flow.
    // Returns `Upload.LIST_IGNORE` so antd neither uploads the file nor
    // stores it in its internal file list.
    const beforeUpload = async (file) => {
        if (!file.type.startsWith('video/')) {
            notifyError(new Error('רק קבצי וידאו (video/*) נתמכים'), 'סוג קובץ לא נתמך');
            return Upload.LIST_IGNORE;
        }
        if (uploading)
            return Upload.LIST_IGNORE;
        // Flip `uploading=true` BEFORE we await, so a second drop during the
        // async auto-create is ignored by the Dragger's `disabled` prop and
        // doesn't double-fire createClip. handleFileChosen also sets this
        // true (idempotent — just overlaps).
        setUploading(true);
        const clip = await ensureClipCreated();
        if (!clip) {
            setUploading(false);
            return Upload.LIST_IGNORE;
        }
        void handleFileChosen(file, clip);
        return Upload.LIST_IGNORE;
    };
    return (_jsx(Modal, { title: effectiveClip ? 'עריכת קליפ' : 'יצירת קליפ', open: open, onCancel: closeModal, onOk: submit, okText: "\u05E9\u05DE\u05D5\u05E8", cancelText: "\u05D1\u05D8\u05DC", confirmLoading: submitting, okButtonProps: { disabled: uploading }, cancelButtonProps: { disabled: uploading }, destroyOnHidden: true, children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, initialValues: initialValues, children: [_jsx(Form.Item, { name: "title", label: "\u05DB\u05D5\u05EA\u05E8\u05EA", rules: [{ required: true, message: 'כותרת נדרשת' }], children: _jsx(Input, {}) }), _jsx(Form.Item, { label: "\u05D4\u05E2\u05DC\u05D0\u05EA \u05D5\u05D9\u05D3\u05D0\u05D5", children: _jsxs(Space, { direction: "vertical", style: { width: '100%' }, size: "small", children: [_jsxs(Upload.Dragger, { name: "file", accept: "video/*", multiple: false, maxCount: 1, showUploadList: false, disabled: uploading, beforeUpload: beforeUpload, style: { borderRadius: 12 }, children: [_jsx("p", { className: "ant-upload-drag-icon", style: { marginBottom: 6 }, children: _jsx(InboxOutlined, { style: { color: '#8B2FD6' } }) }), _jsx("p", { className: "ant-upload-text", style: { fontWeight: 600 }, children: "\u05D2\u05E8\u05E8\u05D5 \u05DC\u05DB\u05D0\u05DF \u05E7\u05D5\u05D1\u05E5 \u05D5\u05D9\u05D3\u05D0\u05D5 \u05D0\u05D5 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05D1\u05D7\u05D9\u05E8\u05D4" }), _jsxs("p", { className: "ant-upload-hint", style: { fontSize: 12 }, children: ["\u05E7\u05D5\u05D1\u05E5 \u05D1\u05D5\u05D3\u05D3 \u05D1\u05DC\u05D1\u05D3 (video/*). \u05D4\u05D4\u05E2\u05DC\u05D0\u05D4 \u05D4\u05D9\u05D0 \u05D9\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05DC Bunny Stream \u05D3\u05E8\u05DA TUS \u05D5-resumable.", !effectiveClip && ' הקליפ יישמר אוטומטית לפני תחילת ההעלאה (ודאו שהכותרת מלאה).'] })] }), _jsxs(Space, { children: [uploading && (_jsx(Button, { danger: true, onClick: abortUpload, children: "\u05D1\u05D9\u05D8\u05D5\u05DC \u05D4\u05E2\u05DC\u05D0\u05D4" })), uploadedGuid && !uploading && (_jsxs(Text, { type: "success", children: [_jsx(VideoCameraOutlined, {}), " \u05D4\u05E1\u05E8\u05D8\u05D5\u05DF \u05D4\u05D5\u05E2\u05DC\u05D4"] }))] }), uploading && (_jsx(Progress, { percent: uploadPct, status: "active" })), uploadError && !uploading && (_jsx(Alert, { type: "error", showIcon: true, message: "\u05D4\u05D4\u05E2\u05DC\u05D0\u05D4 \u05E0\u05DB\u05E9\u05DC\u05D4", description: uploadError }))] }) }), _jsx(Form.Item, { name: "bunny_video_id", label: "Bunny video ID", tooltip: "\u05DE\u05EA\u05E2\u05D3\u05DB\u05DF \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA \u05D0\u05D7\u05E8\u05D9 \u05D4\u05E2\u05DC\u05D0\u05D4. \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D4\u05D3\u05D1\u05D9\u05E7 \u05DE\u05D6\u05D4\u05D4 \u05E7\u05D9\u05D9\u05DD \u05D9\u05D3\u05E0\u05D9\u05EA.", children: _jsx(Input, { placeholder: "\u05DC\u05DE\u05E9\u05DC: 1234-abcd-..." }) })] }, formKey) }));
}
